import { supabase } from './supabase'
import { fetchAllPaged } from './pagination'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

/**
 * Single source of truth for "this product needs restocking" — out of stock, or
 * on-hand at/below its effective reorder threshold (the higher of reorder_level
 * / reorder_point). Reused by LowStockPage (the `/inventory/low-stock` view) and
 * by the Trading "needs attention" low-stock leaf so the badge count and the
 * page it links to reconcile exactly.
 */
export function productNeedsRestock(
  quantity: number | null | undefined,
  reorderLevel: number | null | undefined,
  reorderPoint: number | null | undefined,
): boolean {
  const qty = quantity ?? 0
  const threshold = Math.max(reorderLevel ?? 0, reorderPoint ?? 0)
  if (qty <= 0) return true
  if (threshold > 0 && qty <= threshold) return true
  return false
}

/**
 * Count of active products needing restock for a tenant. Paginated so it stays
 * accurate past PostgREST's ~1000-row cap (the cross-column reorder comparison
 * can't be pushed into the query, so rows are fetched and filtered here).
 */
export async function fetchLowStockRestockCount(tenantId: string): Promise<number> {
  const rows = await fetchAllPaged<{ id: string; quantity: number | null; reorder_level: number | null; reorder_point: number | null }>(
    (from, to) =>
      db('products')
        .select('id, quantity, reorder_level, reorder_point')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('id', { ascending: true })
        .range(from, to),
  )
  return rows.filter(p => productNeedsRestock(p.quantity, p.reorder_level, p.reorder_point)).length
}

export type LowStockProduct = {
  id: string
  name: string
  sku: string | null
  quantity: number
  reorder_point: number
  reorder_quantity: number
  shortage: number
  status: 'out_of_stock' | 'low' | 'ok'
  preferred_supplier_id: string | null
  preferred_supplier_name: string | null
}

export async function checkLowStock(tenantId: string): Promise<LowStockProduct[]> {
  const { data, error } = await db('products')
    .select(`
      id, name, sku, quantity, reorder_point, reorder_level, reorder_quantity,
      preferred_supplier_id,
      suppliers:preferred_supplier_id ( name, business_name )
    `)
    .eq('tenant_id', tenantId)
    .eq('is_active', true)
    .order('name')

  if (error) throw error

  const all = (data ?? []) as Array<{
    id: string; name: string; sku: string | null
    quantity: number; reorder_point: number; reorder_level: number; reorder_quantity: number
    preferred_supplier_id: string | null
    suppliers: { name: string; business_name: string | null } | null
  }>

  return all.map(p => {
    const qty = p.quantity ?? 0
    // Use the higher of reorder_point or reorder_level as the effective threshold
    const threshold = Math.max(p.reorder_point ?? 0, p.reorder_level ?? 0)

    let status: 'out_of_stock' | 'low' | 'ok'
    if (qty === 0) status = 'out_of_stock'
    else if (threshold > 0 && qty <= threshold) status = 'low'
    else status = 'ok'

    return {
      id: p.id,
      name: p.name,
      sku: p.sku,
      quantity: qty,
      reorder_point: threshold,
      reorder_quantity: p.reorder_quantity ?? 0,
      shortage: threshold > 0 ? Math.max(0, threshold - qty) : 0,
      status,
      preferred_supplier_id: p.preferred_supplier_id,
      preferred_supplier_name: p.suppliers ? (p.suppliers.business_name || p.suppliers.name) : null,
    }
  })
}
